import { laptopMediumMax } from '@propertypal/shared/src/constants/mediaQueries';
import useWindowSize from '@propertypal/shared/src/hooks/useWindowSize';
import {
  DEFAULT_TREND_SELECTED,
  MARKET_TREND_COLOURS,
  MarketTrendsResponse,
  PriceIndexLevel,
  TREND_TYPES,
  MarketTrendPointFigure,
} from '@propertypal/shared/src/types/marketTrends';
import { roundOff } from '@propertypal/shared/src/utils/number';
import { getFormattedPrice } from '@propertypal/shared/src/utils/property';
import { formatPropertyAreaHistory, ELECTORAL_AREA_LINK } from '@propertypal/shared/src/utils/property/chartUtils';
import getAvgPriceData from '@propertypal/shared/src/utils/property/getAvgPriceData';
import getAvgPriceLabels from '@propertypal/shared/src/utils/property/getAvgPriceLabels';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import LineChart from '../charts/LineChart';
import Checkbox from '../inputs/Checkbox';
import useMediaQuery from '../media-query/useMediaQuery';
import { LinkText, SubHeading, Text } from '../typography';
import { ChartContent, Checkboxes, Container, Disclaimer } from './AreaPriceHistory.style';
import { Content, LoadingBox, MarketTrendsCheckboxLabel, StatsContent, StyleStats } from './MarketTrends.style';

interface AreaPriceHistoryProps {
  data: MarketTrendsResponse;
}

const AreaPriceHistory: React.FC<AreaPriceHistoryProps> = ({ data }) => {
  const theme = useTheme();
  const [selected, setSelected] = useState<PriceIndexLevel[]>(DEFAULT_TREND_SELECTED);
  const isMobile = useMediaQuery({ query: `(${laptopMediumMax})` });

  const lineData = useMemo(() => formatPropertyAreaHistory(data.data, selected, 24), [selected, data.data, isMobile]);

  const { avgPriceData, checkboxLabels, areaText } = useMemo(
    () => ({
      avgPriceData: getAvgPriceData(data.averagePriceGrowth),
      checkboxLabels: getAvgPriceLabels(data.data),
      areaText: `for ${data.bedrooms} Bed ${data?.propertyStyleGroup?.text}`,
    }),
    [data.data],
  );

  // Check that a particular level has changes and that all points on a level has a value
  const hasFullData = useCallback(
    (level: PriceIndexLevel) => {
      // Check level has an overall change
      if (avgPriceData?.[level]?.change) {
        // then check that level points all have values, and that none are null
        const points = data.data.find((d) => d.level === level)?.points;
        if (points) {
          const ld = Object.values(points) as MarketTrendPointFigure[];
          if (ld?.length) {
            return !ld.find((d) => d.value == null);
          }
        }
      }
      return false;
    },
    [avgPriceData, data.data],
  );

  const windowSize = useWindowSize();

  // Determine the initial selection
  // Preventing default selection to be area without data
  useEffect(() => {
    if (hasFullData('OUTCODE')) setSelected(['OUTCODE']);
    else if (hasFullData('ELECTORAL_AREA')) setSelected(['ELECTORAL_AREA']);
    else if (hasFullData('COUCIL_AREA')) setSelected(['COUCIL_AREA']);
    else if (hasFullData('COUNTRY')) setSelected(['COUNTRY']);
    else setSelected(DEFAULT_TREND_SELECTED);
  }, [avgPriceData, data.data]);

  if (windowSize.width === undefined) return null;

  const chartWidth = isMobile ? windowSize.width - 50 : Math.min(1440 - 360, windowSize.width - 420);
  const chartHeight = isMobile ? 350 : 450;

  return (
    <Container data-testid="area-price-history">
      <Content>
        <ChartContent>
          {lineData?.length > 0 && selected.length > 0 && (
            <SubHeading fontWeight="normal" color={theme.primary} align="center" data-testid="area-price-heading">
              Average Prices {areaText}, {lineData[0].data[0].key} - {lineData[0].data[lineData[0].data.length - 1].key}
            </SubHeading>
          )}
          <Checkboxes>
            {TREND_TYPES.map((t) => (
              <Checkbox
                key={t}
                name={t}
                onValueChange={(checked) =>
                  setSelected((prev) => {
                    if (checked) return [...prev, t];
                    return prev.filter((item) => item !== t);
                  })
                }
                value={selected.includes(t)}
                label={
                  <MarketTrendsCheckboxLabel colour={MARKET_TREND_COLOURS[t]}>
                    {checkboxLabels[t]}
                  </MarketTrendsCheckboxLabel>
                }
                testID={`${t}-checkbox`}
              />
            ))}
          </Checkboxes>
          {!lineData?.length ? (
            <LoadingBox data-testid="area-price-no-data" bg={theme.backgroundLighter} minHeight={chartHeight + 45}>
              <Text mt={15} mb={15}>
                {selected.length === 0
                  ? 'Select an area to view average price history'
                  : 'Northern Ireland House Prices Data Unavailable'}
              </Text>
            </LoadingBox>
          ) : (
            <LineChart
              bubbleTextFormatter={(value) => {
                if (value == null) return `- *`;
                return getFormattedPrice(roundOff(value, 2), '£');
              }}
              labelFormatter={(value) => getFormattedPrice(value, '£')}
              width={chartWidth}
              height={chartHeight}
              lines={lineData}
              boldAxis
              hideYAxisLine
              bubbleAutoHeight
              noDataLegend
              dottedPoints
              noDataText="No data available due to low sample size"
            />
          )}
        </ChartContent>
        <StatsContent spaceAround>
          <Text mb={5} fontWeight="bold">
            Average price growth {areaText}: {avgPriceData.COUNTRY?.label}
          </Text>
          {TREND_TYPES.map((key) => {
            if (avgPriceData[key] != null) {
              return (
                <StyleStats key={key}>
                  <div>
                    <Text mb={5}>{checkboxLabels[key]}</Text>
                    <Text fontSize={isMobile ? 24 : 30} color={MARKET_TREND_COLOURS[key]}>
                      {avgPriceData?.[key]?.change == null ? '- *' : `${avgPriceData?.[key]?.change}%`}
                    </Text>
                  </div>
                </StyleStats>
              );
            }
            return null;
          })}
          {Object.values(avgPriceData).some((p) => p?.change == null) && (
            <Text fontSize={12} style={{ marginTop: 'auto' }}>
              *Undisclosed due to low sample size
            </Text>
          )}
        </StatsContent>
      </Content>
      <Disclaimer mt={50}>
        <Text fontWeight="bold" fontSize={14}>
          DISCLAIMER
        </Text>

        <Text fontSize={12} mt={10}>
          Unless otherwise stated, all content in this section is copyright of PropertyPal 2019, and all rights are
          reserved. The contents of this section, either in whole or in part, may not be reproduced, stored or
          transmitted without prior written permission from PropertyPal. This section has been prepared by PropertyPal
          for general guidance on matters of interest only, and does not constitute professional advice. You should not
          act upon the information contained in this section without obtaining specific professional advice. No
          representation or warranty (express or implied) is given as to the accuracy or completeness of the information
          contained in this section. It is a condition of PropertyPal allowing you free access to this section that you
          accept that PropertyPal, its clients, employees and agents do not accept or assume any liability,
          responsibility or duty of care for any consequences of you or anyone else acting, or refraining to act, in
          reliance on the information contained in this section or for any decision based on it.
        </Text>
        <Text fontSize={12} mt={10}>
          *Note: The dataset is compiled by PropertyPal with statistical analyses performed using listed price
          information which has been cleansed to remove anomalies. Locations provided are based on the latest electoral
          area boundaries provided by the District Electoral Areas Commissioner and can be accessed{' '}
          <LinkText href={ELECTORAL_AREA_LINK} target="_blank" rel="noreferrer">
            here
          </LinkText>
          .
        </Text>
      </Disclaimer>
    </Container>
  );
};

export default AreaPriceHistory;
